import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import Lightbox from '../components/Lightbox';
import FaqAccordion from '../components/FaqAccordion';
import { imageUrl } from '../lib/images';
import { createProseLinker } from '../lib/proseLinks';
import { generateEventId, trackMetaLead } from '../lib/metaTracking';

// Helper to parse HTML FAQ into structured items for FaqAccordion
function parseFaqHtml(htmlString) {
  if (!htmlString || typeof htmlString !== 'string') return [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    let questionNodes = Array.from(doc.querySelectorAll('h4'));
    if (questionNodes.length === 0) {
      questionNodes = Array.from(doc.querySelectorAll('h3')).filter(
        (h) => !/ofte stillede spørgsmål|faq/i.test(h.textContent.trim())
      );
    }

    if (questionNodes.length > 0) {
      const items = [];
      questionNodes.forEach((qNode) => {
        const question = qNode.textContent.trim();
        if (!question) return;
        const answerParts = [];
        let next = qNode.nextElementSibling;
        while (next && !['h2', 'h3', 'h4'].includes(next.tagName.toLowerCase())) {
          const text = next.textContent.trim();
          if (text) answerParts.push(text);
          next = next.nextElementSibling;
        }
        if (answerParts.length > 0) {
          items.push({
            question,
            answer: answerParts.join('\n\n')
          });
        }
      });
      if (items.length > 0) return items;
    }
  } catch (err) {
    console.error('Kunne ikke parse FAQ HTML:', err);
  }
  return [];
}

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const faqItems = useMemo(() => {
    return parseFaqHtml(service?.faq_html);
  }, [service?.faq_html]);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Form submission state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const eventIdRef = useRef(null);

  const proseLinker = useMemo(() => createProseLinker(`/ydelser/${slug}`), [slug]);

  const formattedDescription = useMemo(() => {
    if (!service?.description) return '<p>Kontakt os for yderligere information.</p>';
    return proseLinker.formatHtml(service.description);
  }, [service?.description, proseLinker]);

  useEffect(() => {
    setLoading(true);
    setSubmitted(false);
    setErrorMsg('');

    // Fetch all services to find matching slug
    fetch('/_api/public/content/ydelser')
      .then((res) => res.json())
      .then((data) => {
        const items = Array.isArray(data) ? data : data.items || [];
        const match = items.find((s) => {
          const sSlug = s.slug?._ || s.slug || '';
          return sSlug === slug || s.id === slug || s.legacy_url?.includes(slug);
        });

        if (match) {
          setService(match);

          // If service has a gallery_slug, dynamically fetch that gallery from CMS
          if (match.gallery_slug) {
            fetch('/_api/public/content/galleri')
              .then((res) => res.json())
              .then((gData) => {
                const gItems = Array.isArray(gData) ? gData : gData.items || [];
                const gMatch = gItems.find((g) => {
                  const gSlug = g.slug?._ || g.slug || '';
                  return gSlug === match.gallery_slug || g.title?.toLowerCase() === match.gallery_slug?.toLowerCase();
                });
                if (gMatch && Array.isArray(gMatch.images)) {
                  setGalleryImages(gMatch.images);
                }
              })
              .catch(() => {});
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Fejl ved hentning af ydelse:', err);
        setLoading(false);
      });
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');

    if (!eventIdRef.current) {
      eventIdRef.current = generateEventId();
    }
    const currentEventId = eventIdRef.current;

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        message: `Henvendelse vedrørende: ${service?.title || 'Ydelse'}\n\nBesked:\n${formData.message}`,
        website_url: formData.website_url, // Honeypot
        service_subject: service?.title || ''
      };

      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error('Kunne ikke indsende formularen. Prøv venligst igen.');
      }

      await trackMetaLead({
        eventId: currentEventId,
        email: formData.email,
        phone: formData.phone,
        contentName: `Ydelse: ${service?.title || slug}`
      });

      eventIdRef.current = null;
      setSubmitted(true);
      setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
    } catch (err) {
      setErrorMsg(err.message || 'Der opstod en fejl ved afsendelse. Ring venligst til 70 700 800.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center py-20 text-zinc-500 font-mono text-sm">
        <i className="fa-solid fa-spinner fa-spin text-3xl mb-3 text-red-600" aria-hidden="true" />
        <p>Henter oplysninger om ydelsen...</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center py-20 px-4 text-center">
        <h1 className="font-condensed text-4xl font-bold uppercase text-zinc-900 mb-2">
          Ydelse ikke fundet
        </h1>
        <p className="text-zinc-600 mb-6">Den efterspurgte ydelse findes ikke eller er blevet flyttet.</p>
        <Link
          to="/ydelser"
          className="px-6 py-3 bg-red-600 text-white font-condensed text-lg uppercase font-bold rounded shadow hover:bg-red-700"
        >
          Se alle ydelser
        </Link>
      </div>
    );
  }

  const heroImg = service.hero_image?.[0] || 'https://assets.auxodev.dk/assets/7ab2fd9d-1ad5-42a7-a15d-22229bea7818/roed-volvo-autotransport-med-7-biler-18-03-2025-379.jpeg';

  return (
    <>
      <SEOHead
        title={service.meta_title || `${service.title} | MC Transport`}
        description={service.meta_description || service.short_description}
      />

      {/* Breadcrumbs */}
      <nav aria-label="Brødkrummer" className="bg-zinc-100 border-b border-zinc-200 py-2.5 px-4 sm:px-6 lg:px-8 text-xs text-zinc-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="hover:text-red-600">Forside</Link>
          <span className="text-zinc-400">/</span>
          <Link to="/ydelser" className="hover:text-red-600">Ydelser</Link>
          <span className="text-zinc-400">/</span>
          <span className="text-zinc-900 font-semibold truncate">{service.title}</span>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-zinc-950 text-white py-16 lg:py-24 overflow-hidden border-b border-zinc-800">
        <img
          src={imageUrl(heroImg, 'width=1920,quality=80,format=auto')}
          alt={`${service.title} - MC Transport`}
          className="absolute inset-0 w-full h-full object-cover object-center brightness-[0.35]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/50 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            {service.badge && (
              <span className="inline-block px-3 py-1 bg-red-600 text-white text-xs font-bold uppercase tracking-wider rounded mb-3">
                {service.badge}
              </span>
            )}
            <h1 className="font-condensed text-4xl sm:text-6xl font-extrabold uppercase tracking-tight text-white leading-tight">
              {service.title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-zinc-200 leading-relaxed font-normal">
              {service.short_description ? proseLinker.format(service.short_description, {
                companyLinkClass: "font-semibold text-white hover:text-red-400 underline decoration-red-500 underline-offset-2 transition-colors",
                linkClass: "text-red-400 font-medium hover:text-red-300 underline decoration-red-400/40 underline-offset-2 transition-colors"
              }) : 'Professionel og driftssikker transportløsning i hele Danmark og Europa.'}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="tel:+4570700800"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 bg-red-600 hover:bg-red-700 text-white font-condensed text-xl font-bold uppercase rounded shadow-xl whitespace-nowrap"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>Ring vagtcentral: +45 70 700 800</span>
              </a>
              <a
                href="#kontaktformular"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-zinc-900/90 hover:bg-zinc-800 text-white font-condensed text-xl font-bold uppercase rounded border border-white/20 transition-colors"
              >
                <span>Få et tilbud herunder</span>
                <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left 8 Cols: Description, Gallery, FAQ */}
            <div className="lg:col-span-8 space-y-12">
              {/* Verbatim Rich text content with internal linking */}
              <div
                className="prose prose-zinc max-w-none text-zinc-800 text-base leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: formattedDescription }}
              />

              {/* DYNAMIC CMS GALLERY (via technical gallery_slug reference) */}
              {galleryImages.length > 0 && (
                <div className="pt-6 border-t border-zinc-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-condensed text-2xl font-bold uppercase text-zinc-950">
                      Billeder af materiel og arbejde
                    </h3>
                    <span className="text-xs text-zinc-500 font-mono">
                      {galleryImages.length} fotos · Tryk for stor visning
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {galleryImages.map((gImg, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setLightboxIndex(idx);
                          setLightboxOpen(true);
                        }}
                        className="group relative aspect-4/3 rounded-lg overflow-hidden bg-zinc-950 border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-red-600 shadow-sm"
                      >
                        <img
                          src={imageUrl(gImg, 'width=500,height=375,fit=cover,quality=80,format=auto')}
                          alt={`${service.title} - MC Transport galleri foto ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 brightness-95"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <span className="w-8 h-8 rounded-full bg-red-600/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="fa-solid fa-magnifying-glass-plus text-xs" aria-hidden="true" />
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ Section */}
              {service.faq_html && (
                <div className="pt-8 border-t border-zinc-200">
                  <div className="mb-5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-red-600 font-condensed bg-red-50 px-2.5 py-1 rounded">
                      <i className="fa-solid fa-circle-question" aria-hidden="true" />
                      Spørgsmål & Svar
                    </span>
                    <h3 className="font-condensed text-2xl sm:text-3xl font-bold uppercase text-zinc-950 mt-2">
                      Ofte stillede spørgsmål om {service.title.toLowerCase()}
                    </h3>
                  </div>

                  {faqItems.length > 0 ? (
                    <FaqAccordion items={faqItems} variant="light" />
                  ) : (
                    <div
                      className="p-6 bg-white rounded-xl border border-zinc-200 shadow-sm text-zinc-700 leading-relaxed text-sm [&_h3]:hidden [&_h4]:font-bold [&_h4]:text-zinc-950 [&_h4]:text-base [&_h4]:mt-4 [&_h4]:first:mt-0 [&_h4]:mb-1.5 [&_p]:text-zinc-600 [&_p]:mb-3 [&_p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{ __html: service.faq_html }}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Right 4 Cols: Quick Contact Box & Service inquiry form */}
            <div id="kontaktformular" className="lg:col-span-4 space-y-6">
              {/* Emergency Call Box */}
              <div className="bg-zinc-950 text-white p-6 rounded-xl border border-zinc-800 shadow-lg">
                <div className="flex items-center gap-2 text-red-500 text-xs font-bold uppercase tracking-wider mb-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span>Døgnvagt 24/7/365</span>
                </div>
                <h4 className="font-condensed text-2xl font-bold uppercase text-white">
                  Brug for akut hjælp?
                </h4>
                <p className="text-xs text-zinc-400 mt-1 mb-4">
                  Vores vagtcentral tager telefonen døgnet rundt.
                </p>
                <a
                  href="tel:+4570700800"
                  className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-red-600 hover:bg-red-700 text-white font-condensed text-xl font-bold uppercase rounded shadow transition-all whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span>Ring +45 70 700 800</span>
                </a>
              </div>

              {/* Form with pre-selected service */}
              <div className="bg-zinc-50 p-6 rounded-xl border border-zinc-200 shadow-sm">
                <h4 className="font-condensed text-xl font-bold uppercase text-zinc-900 mb-1">
                  Få et tilbud på {service.title}
                </h4>
                <p className="text-xs text-zinc-600 mb-4">
                  Udfyld formularen, så vender vi hurtigt tilbage med en skræddersyet pris.
                </p>

                {submitted ? (
                  <div className="p-4 bg-emerald-50 border border-emerald-300 rounded text-emerald-800 text-sm">
                    <p className="font-bold flex items-center gap-1.5 mb-1">
                      <i className="fa-solid fa-circle-check text-emerald-600" aria-hidden="true" />
                      Tak for din henvendelse!
                    </p>
                    <p className="text-xs">
                      Vi har modtaget din forespørgsel vedrørende <strong>{service.title}</strong> og kontakter dig hurtigst muligt.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-3">
                    {/* Honeypot field (strictly hidden with sr-only + absolute) */}
                    <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                      <label htmlFor="website_url">Hjemmeside url (lad stå tom)</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        tabIndex={-1}
                        autoComplete="off"
                        value={formData.website_url}
                        onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-zinc-700 mb-1">
                        Dit Navn *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Fornavn og efternavn"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full text-xs p-2.5 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-zinc-700 mb-1">
                        Telefonnummer *
                      </label>
                      <input
                        type="tel"
                        required
                        placeholder="f.eks. 12 34 56 78"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full text-xs p-2.5 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-zinc-700 mb-1">
                        E-mail *
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="din@email.dk"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full text-xs p-2.5 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-zinc-700 mb-1">
                        Opgavebeskrivelse *
                      </label>
                      <textarea
                        required
                        rows={4}
                        placeholder={`Beskriv opgaven for ${service.title} (hvorfra, hvortil, biltype...)`}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full text-xs p-2.5 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                      />
                    </div>

                    {errorMsg && (
                      <p className="text-xs text-red-600 font-semibold">{errorMsg}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 px-4 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 text-white font-condensed text-base font-bold uppercase tracking-wider rounded shadow transition-colors"
                    >
                      {submitting ? 'Sender forespørgsel...' : 'Send forespørgsel'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Component */}
      <Lightbox
        images={galleryImages}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onSelectIndex={(idx) => setLightboxIndex(idx)}
      />
    </>
  );
}
